import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { PageHeader } from '@components/common';
import { SearchBar } from '@components/common/SearchBar';
import {
  useMasterScriptsQuery,
  useCreateScript,
  useDeleteScripts
} from '../hooks/useScripts';
import CreateScriptDialog from '../components/CreateScriptDialog';
import { ScriptHelpDialog } from '../components/ScriptHelpDialog';
import { useTranslation } from 'react-i18next';
import type { Script } from '../types';

const ScriptsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { data, isLoading, isError } = useMasterScriptsQuery();
  const createScript = useCreateScript();
  const deleteScripts = useDeleteScripts();

  const scripts = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search) return scripts;
    const lower = search.toLowerCase();
    return scripts.filter(s => s.globalId.toLowerCase().includes(lower));
  }, [scripts, search]);

  const selectionMode = selectedIds.size > 0;

  const allFilteredSelected =
    filtered.length > 0 && filtered.every(s => selectedIds.has(s.id));

  const handleToggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(s => s.id)));
    }
  }, [allFilteredSelected, filtered]);

  const handleConfirmDelete = useCallback(() => {
    deleteScripts.mutate([...selectedIds], {
      onSuccess: () => {
        setSelectedIds(new Set());
        setDeleteDialogOpen(false);
      },
      onSettled: () => {
        setDeleteDialogOpen(false);
      }
    });
  }, [selectedIds, deleteScripts]);

  const handleCreate = (name: string, scriptData: string) => {
    createScript.mutate(
      {
        globalId: name,
        scriptType: 'MASTER',
        scriptData,
        createdBy: 'system'
      },
      {
        onSuccess: script => {
          setCreateDialogOpen(false);
          navigate(`/scripts/${script.id}`);
        }
      }
    );
  };

  const handleCardClick = (script: Script) => {
    if (selectionMode) return;
    navigate(`/scripts/${script.id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('scripts.title', 'Scripts')}
        action={
          selectionMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('scripts.selected', '{{count}} selected', {
                  count: selectedIds.size
                })}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleSelectAll}
              >
                {allFilteredSelected
                  ? t('buttons.clearAll', 'Clear All')
                  : t('buttons.selectAll', 'Select All')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteScripts.isPending}
              >
                {t('buttons.delete', 'Delete')} ({selectedIds.size})
              </Button>
              <Button variant="text" onClick={() => setSelectedIds(new Set())}>
                {t('buttons.cancel', 'Cancel')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setHelpOpen(true)}
                size="small"
              >
                {t('scripts.help', 'Help')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                disabled={createScript.isPending}
              >
                {t('scripts.createScript', 'Create Script')}
              </Button>
            </Box>
          )
        }
      />

      <Box sx={{ mb: 3, maxWidth: 400 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Filter scripts..."
        />
      </Box>

      {isError && (
        <Typography variant="body2" color="error">
          Failed to load scripts
        </Typography>
      )}

      {filtered.length === 0 && !isError && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DescriptionOutlinedIcon
            sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {search ? 'No scripts match your filter' : 'No scripts yet'}
          </Typography>
          {!search && (
            <Typography variant="body2" color="text.disabled">
              Create a master script to get started
            </Typography>
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {filtered.map(script => (
          <Grid item xs={12} sm={6} md={4} key={script.id}>
            <Card
              sx={{
                cursor: selectionMode ? 'default' : 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
                ...(selectedIds.has(script.id) && {
                  outline: '2px solid',
                  outlineColor: 'primary.main'
                }),
                '&:hover': {
                  boxShadow: 3,
                  ...(!selectionMode && { transform: 'translateY(-2px)' })
                },
                '&:active': {
                  ...(!selectionMode && { transform: 'translateY(0)' })
                }
              }}
              onClick={() => handleCardClick(script)}
            >
              <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      mr: 1,
                      minWidth: 0
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(script.id)}
                      onClick={e => handleToggleSelect(script.id, e)}
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {script.globalId}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {script.createdBy}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CalendarTodayIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(script.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        ml: 'auto'
                      }}
                    >
                      <AccountTreeIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {script.childCount}{' '}
                        {script.childCount === 1 ? 'item' : 'items'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ScriptHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <CreateScriptDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreate}
        isPending={createScript.isPending}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t('scripts.deleteConfirmTitle', 'Delete Scripts')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'scripts.deleteConfirmMessage',
              'Are you sure you want to delete {{count}} script(s)? This action cannot be undone.',
              { count: selectedIds.size }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteScripts.isPending}
          >
            {t('buttons.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteScripts.isPending}
            startIcon={
              deleteScripts.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {t('buttons.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScriptsListPage;
